import React, { memo, useMemo } from 'react';
import { UserOutlined } from "@ant-design/icons";
import { Tooltip } from 'antd';
import HTMLReactParser from 'html-react-parser';
import DOMPurify from 'dompurify';
import { displayTimestamp } from '../../utils/displayTimestamp';

const ALLOWED_TAGS = [
    'b', 'i', 'u', 'em', 'strong', 'span', 'p', 'br',
    'ul', 'ol', 'li', 'a', 'div', 'blockquote', 'code',
    'pre', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
];
const ALLOWED_ATTR = ['class', 'style', 'href', 'target', 'rel', 'data-mention-id'];

// The mention-tag feature encodes its highlight as this specific inline background-color;
// everything else in `style` is stripped since arbitrary backgrounds (e.g. pasted from
// Word/Outlook) can render authored text unreadable against the message bubble's own background.
const MENTION_BACKGROUND_REGEX = /background-color:\s*rgba\(123,\s*173,\s*126/i;
const BACKGROUND_PROPERTY_REGEX = /^background/i;

const sanitizeStyleAttribute = (style) => style
    .split(';')
    .map((declaration) => declaration.trim())
    .filter((declaration) => (
        declaration
        && (!BACKGROUND_PROPERTY_REGEX.test(declaration) || MENTION_BACKGROUND_REGEX.test(declaration))
    ))
    .join('; ');

if (typeof DOMPurify.addHook === 'function') {
    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
        if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
            node.setAttribute('rel', 'noreferrer noopener');
        }
        const style = node.getAttribute && node.getAttribute('style');
        if (style) {
            const sanitizedStyle = sanitizeStyleAttribute(style);
            if (sanitizedStyle) {
                node.setAttribute('style', sanitizedStyle);
            } else {
                node.removeAttribute('style');
            }
        }
    });
}

const sanitizeMessageHtml = (html) => DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'link', 'meta', 'form', 'input'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus'],
});

import {
    Msg,
    StyledAvatar,
    MsgBubble,
    MsgInfo,
    MsgInfoName,
    MsgInfoTime,
    MsgText,
    ImageWrapper,
    Image
} from './ChatMessage.styles';

const MENTION_REGEX = /^@(\w+)$/;

const isMentionSpan = (domNode) => {
    if (!domNode || domNode.type !== 'tag' || domNode.name !== 'span') return false;
    const { class: className, 'data-mention-id': mentionId, style } = domNode.attribs || {};
    if (style && MENTION_BACKGROUND_REGEX.test(style)) return true;
    if (className && className.split(/\s+/).includes('mention-tag')) return true;
    return Boolean(mentionId);
};

const getMentionText = (domNode) => {
    const child = domNode.children?.[0];
    if (!child || child.type !== 'text') return null;
    const text = child.data?.trim();
    return MENTION_REGEX.test(text) ? text : null;
};

const resolveMentionUser = (domNode, mentionText, adminUsers) => {
    const mentionId = domNode.attribs?.['data-mention-id'];
    if (mentionId) {
        const byId = adminUsers.find(user => user._id === mentionId);
        if (byId) return byId;
    }
    const mentionFirstName = mentionText.substring(1).toLowerCase();
    return adminUsers.find(user => {
        const userFirstName = user.firstName || user.email?.split('@')[0] || '';
        return userFirstName.toLowerCase() === mentionFirstName;
    });
};

const buildMentionReplacer = (adminUsers = []) => (domNode) => {
    if (!isMentionSpan(domNode)) return undefined;
    const mentionText = getMentionText(domNode);
    if (!mentionText) return undefined;

    const matchedUser = resolveMentionUser(domNode, mentionText, adminUsers);
    if (!matchedUser) return undefined;

    const fullName = `${matchedUser.firstName || ''} ${matchedUser.lastName || ''}`.trim()
        || matchedUser.email
        || mentionText;

    return (
        <Tooltip title={fullName}>
            <span
                className="mention-tag"
                style={{ fontWeight: 'bold' }}
            >
                {mentionText}
            </span>
        </Tooltip>
    );
};

function renderAvatar(avatar, isOwnMessage) {
    const margin = isOwnMessage ? 'left' : 'right';
    if (avatar) {
        return <StyledAvatar size="large" src={avatar} margin={margin} />
    }
    return <StyledAvatar size="large" icon={<UserOutlined />} margin={margin} />
}

export const ChatMessage = memo(({
    avatar,
    timestamp,
    handleMarkTicketAsRead,
    isOwnMessage,
    isUnread,
    contentSrc,
    message,
    authorName,
    isSending = false,
    adminUsers,
}) => {

    const processedMessage = useMemo(() => {
        if (!message) return null;
        const sanitized = sanitizeMessageHtml(message);
        return HTMLReactParser(sanitized, { replace: buildMentionReplacer(adminUsers) });
    }, [message, adminUsers]);

    const renderRight = isOwnMessage || isSending;

    return (
        <Msg
            align={renderRight ? 'right' : 'left'}
            key={timestamp}
            onClick={() => isUnread && handleMarkTicketAsRead()}
        >
            {renderAvatar(avatar, renderRight)}
            <MsgBubble isOwnMessage={renderRight} isUnread={isUnread} isSending={isSending}>
                <MsgInfo>
                    <MsgInfoName>{authorName}</MsgInfoName>
                    <MsgInfoTime>
                        {isSending ? 'Sending...' : displayTimestamp(timestamp)}
                    </MsgInfoTime>
                </MsgInfo>
                {
                    contentSrc && (
                        <ImageWrapper>
                            <Image src={contentSrc} alt="uploaded-content-view" />
                        </ImageWrapper>
                    )
                }
                {
                    processedMessage && (
                        <MsgText isOwnMessage={renderRight}>
                            {processedMessage}
                        </MsgText>
                    )
                }
            </MsgBubble>
        </Msg>
    );
});
